<template>
  <main class="mobile-page service-screen">
    <div class="mobile-screen">
      <header class="mobile-topnav">
        <RouterLink class="mobile-icon-btn" to="/mobile/user" aria-label="返回我的">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M14.5 5.5 8 12l6.5 6.5" /></svg>
        </RouterLink>
        <span class="mobile-topnav-title">我的订单</span>
        <i class="nav-space"></i>
      </header>

      <section class="page-hero">
        <span class="mobile-eyebrow">Orders · 官网交易</span>
        <h1>订单进度随时可查</h1>
        <p>集中查看 VitaeAI 数字员工系统订单的支付、审核和交付状态。</p>
      </section>

      <div v-if="loading" class="mobile-card loading-card" aria-live="polite">正在加载订单...</div>
      <div v-else-if="message" class="mobile-card message-card" role="alert">
        <p>{{ message }}</p>
        <button class="mobile-btn mobile-btn-primary" type="button" @click="loadOrders">重新加载</button>
      </div>
      <div v-else-if="orders.length === 0" class="mobile-card empty-card">
        <strong>暂无官网订单</strong>
        <p>下单后可以在这里跟踪支付、审核、发货和收货进度。</p>
      </div>

      <template v-else>
        <section class="summary-grid">
          <div class="mobile-card summary-card">
            <span>全部订单</span>
            <strong>{{ orders.length }}</strong>
          </div>
          <div class="mobile-card summary-card">
            <span>待处理</span>
            <strong>{{ activeCount }}</strong>
          </div>
        </section>

        <RouterLink
          v-for="order in orders"
          :key="order.id"
          class="mobile-card order-card"
          :to="`/mobile/orders/${order.id}`"
        >
          <div class="order-top">
            <div>
              <small>订单编号</small>
              <strong>{{ order.orderNo || '-' }}</strong>
            </div>
            <span class="status-pill" :class="statusClass(order.orderStatus)">{{ statusText(order.orderStatus) }}</span>
          </div>
          <div class="order-meta">
            <span>下单时间 · {{ formatDate(order.createTime) }}</span>
            <span>{{ payText(order) }}</span>
            <span v-if="hasCityPartnerContract(order)">合同 · {{ orderContractStatusText(order) }}</span>
            <span v-if="coolingCountdownText(order)" class="auto-receipt-countdown">{{ coolingCountdownText(order) }}</span>
            <span v-if="autoReceiptText(order)" class="auto-receipt-countdown">{{ autoReceiptText(order) }}</span>
          </div>
          <div class="order-bottom">
            <div>
              <small>订单金额</small>
              <strong>{{ money(order.totalAmount) }}</strong>
            </div>
            <div class="order-actions">
              <em>查看详情 ›</em>
              <CancelOrderAction v-if="canCancelOrder(order)" :order="order" @canceled="loadOrders" />
            </div>
          </div>
        </RouterLink>
      </template>
    </div>
    <MobileTabbar active="user" />
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import MobileTabbar from '@/components/mobile/MobileTabbar.vue'
import CancelOrderAction from '@/components/mobile/CancelOrderAction.vue'
import { getWebsiteOrders } from '@/api/trade'
import { hasCityPartnerContract, orderContractStatusText } from '@/utils/orderContracts'

const router = useRouter()
const orders = ref([])
const now = ref(Date.now())
let countdownTimer = null
const loading = ref(false)
const message = ref('')
const activeCount = computed(() => orders.value.filter(order => ![6, 7].includes(Number(order.orderStatus))).length)

async function loadOrders() {
  if (!localStorage.getItem('dzzj_website_token')) {
    router.push({ path: '/mobile/login', query: { redirect: '/mobile/orders' } })
    return
  }
  loading.value = true
  message.value = ''
  try {
    orders.value = await getWebsiteOrders()
  } catch (error) {
    message.value = error.message || '订单加载失败'
  } finally {
    loading.value = false
  }
}

function statusText(status) {
  return {
    0: '待确认协议',
    1: '待支付',
    2: '待审核',
    3: '待发货',
    4: '已发货',
    5: '收货审核中',
    6: '已收货',
    7: '已关闭',
    8: '冷静期中'
  }[Number(status)] || '未知状态'
}

function statusClass(status) {
  const value = Number(status)
  if (value === 6) return 'done'
  if (value === 7) return 'closed'
  if ([1, 2, 3, 8].includes(value)) return 'pending'
  if ([4, 5].includes(value)) return 'shipping'
  return 'initial'
}

function payText(order) {
  if (Number(order.bankTransferStatus) === 1) return '转账待核对'
  return Number(order.payStatus) === 1 ? '已支付' : '未支付'
}

function canCancelOrder(order) {
  const status = Number(order.orderStatus)
  return Number.isFinite(status) && status >= 0 && status < 3 && Number(order.bankTransferStatus) !== 1
}

function formatDate(value) {
  return value ? String(value).replace('T', ' ').slice(0, 16) : '-'
}

function money(value) {
  const amount = Number(value)
  return Number.isFinite(amount) ? `¥${amount.toFixed(2)}` : '-'
}

/**
 * 判断订单当前是否可申请签收且尚未完成签收。
 * @param order 官网订单
 * @returns {boolean} 是否处于可签收状态
 */
function canConfirmReceipt(order) {
  if (!order || Number(order.payStatus) !== 1 || Number(order.deliveryStatus) !== 1
    || Number(order.orderStatus) >= 6 || !order.deliveryTime) return false
  const auditStatus = Number(order.receiptAuditStatus)
  if (auditStatus === 1 || auditStatus === 2) return false
  const auditDeadline = order.receiptAuditDeadline ? parseDateTime(order.receiptAuditDeadline) : 0
  if (auditStatus === 3 && auditDeadline > now.value) return false
  return receiptAvailableAt(order) <= now.value
}

/**
 * 获取下单后开始计算的收货冷静期截止时间。
 * @param order 官网订单
 * @returns {number} 截止时间戳，缺失时返回0
 */
function receiptAvailableAt(order) {
  const coolingDeadline = parseDateTime(order?.coolingDeadline)
  if (coolingDeadline) return coolingDeadline
  const createTime = parseDateTime(order?.createTime)
  return createTime ? createTime + 7 * 24 * 60 * 60 * 1000 : 0
}

/**
 * 生成自动签收倒计时文案；仅可签收但未签收的订单返回内容。
 * @param order 官网订单
 * @returns {string} 倒计时文案
 */
function autoReceiptText(order) {
  if (!canConfirmReceipt(order)) return ''
  const deadline = Date.parse(String(order.autoReceiptDeadline || order.receiptAutoConfirmDeadline || order.receiptAuditDeadline || '').replace(' ', 'T'))
    || (Math.max(receiptAvailableAt(order), parseDateTime(order.deliveryTime)) + 48 * 60 * 60 * 1000)
  const remaining = deadline - now.value
  if (remaining <= 0) return '自动签收倒计时 0'
  const totalHours = Math.ceil(remaining / 3600000)
  return `自动签收倒计时 ${Math.floor(totalHours / 24)}天 ${String(totalHours % 24).padStart(2, '0')}小时`
}

/**
 * 生成订单冷静期剩余文案；仅已进入冷静期等待的订单返回内容。
 * @param {object} order 官网订单
 * @returns {string} 冷静期倒计时文案
 */
function coolingCountdownText(order) {
  if (!order || Number(order.payStatus) !== 1 || Number(order.orderStatus) >= 6) return ''
  const noDeliveryCooling = Number(order.orderStatus) === 8
  const deliveredCooling = order.needDelivery !== false && Number(order.orderStatus) === 4
  if (!noDeliveryCooling && !deliveredCooling) return ''
  const remaining = receiptAvailableAt(order) - now.value
  if (remaining <= 0) return ''
  const totalHours = Math.ceil(remaining / 3600000)
  return `冷静期倒计时 ${Math.floor(totalHours / 24)}天 ${String(totalHours % 24).padStart(2, '0')}小时`
}

/**
 * 解析后端日期字符串为时间戳。
 * @param value 日期字符串
 * @returns {number} 时间戳，解析失败返回 0
 */
function parseDateTime(value) {
  const timestamp = Date.parse(String(value).replace(' ', 'T'))
  return Number.isNaN(timestamp) ? 0 : timestamp
}

onMounted(() => { loadOrders(); countdownTimer = window.setInterval(() => { now.value = Date.now() }, 1000) })
onBeforeUnmount(() => { if (countdownTimer) window.clearInterval(countdownTimer) })
</script>

<style scoped>
.service-screen { padding-bottom: calc(104px + env(safe-area-inset-bottom)); }
.nav-space { width: 36px; flex: 0 0 36px; }
.page-hero { padding: 24px 2px 18px; }
.page-hero h1 { margin: 12px 0 8px; font-size: 24px; font-weight: 900; line-height: 1.25; }
.page-hero p { color: var(--mobile-muted); font-size: 12px; line-height: 1.7; }
.summary-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); align-items: stretch; gap: 10px; margin-bottom: 12px; }
.summary-grid .mobile-card + .mobile-card { margin-top: 0; }
.summary-card { min-height: 78px; display: flex; flex-direction: column; justify-content: flex-end; padding: 13px 14px; }
.summary-card span { display: block; color: var(--mobile-quiet); font-size: 11px; font-weight: 700; }
.summary-card strong { display: block; margin-top: 4px; font-size: 22px; line-height: 1.2; font-weight: 900; }
.order-card { display: block; color: inherit; text-decoration: none; }
.order-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.order-top small, .order-bottom small { display: block; margin-bottom: 3px; color: var(--mobile-quiet); font-size: 10.5px; font-weight: 700; }
.order-top strong { color: var(--mobile-ink); font-size: 13px; font-weight: 800; word-break: break-all; }
.order-meta { display: flex; flex-wrap: wrap; gap: 6px 12px; margin-top: 13px; padding-top: 12px; border-top: 1px dashed var(--mobile-line); color: var(--mobile-muted); font-size: 11.5px; }
.auto-receipt-countdown { color: var(--mobile-gold); font-weight: 800; }
.order-bottom { display: flex; align-items: flex-end; justify-content: space-between; gap: 12px; margin-top: 14px; }
.order-bottom strong { color: var(--mobile-cyan); font-size: 19px; font-weight: 900; }
.order-actions { display: flex; align-items: center; justify-content: flex-end; gap: 9px; }
.order-bottom em { flex: none; color: var(--mobile-quiet); font-size: 11.5px; font-style: normal; font-weight: 700; }
.status-pill { flex: none; padding: 5px 9px; border-radius: 999px; font-size: 10.5px; font-weight: 800; }
.status-pill.initial { color: var(--mobile-muted); border: 1px solid var(--mobile-line-strong); background: rgba(255,255,255,.04); }
.status-pill.pending { color: var(--mobile-gold); border: 1px solid rgba(255,207,106,.3); background: var(--mobile-gold-soft); }
.status-pill.shipping { color: var(--mobile-violet); border: 1px solid rgba(155,140,255,.3); background: var(--mobile-violet-soft); }
.status-pill.done { color: var(--mobile-cyan); border: 1px solid rgba(98,244,214,.3); background: var(--mobile-cyan-soft); }
.status-pill.closed { color: var(--mobile-faint); border: 1px solid var(--mobile-line); background: rgba(255,255,255,.02); }
.loading-card, .message-card, .empty-card { text-align: center; }
.loading-card { color: var(--mobile-muted); font-size: 12.5px; }
.message-card { display: grid; gap: 12px; }
.message-card p { color: #ff9c9c; font-size: 12.5px; line-height: 1.6; }
.empty-card strong { display: block; margin-bottom: 7px; font-size: 15px; }
.empty-card p { color: var(--mobile-muted); font-size: 12px; line-height: 1.7; }
@media (max-width: 350px) { .summary-grid { grid-template-columns: 1fr; } }
</style>
